package dev.lingvo.ops.state

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.site.api.ApiException
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

internal data class OpsLoginState(
    val email: String = "",
    val password: String = "",
    val failure: String? = null,
    val submitting: Boolean = false,
    val completed: Boolean = false,
)

internal sealed interface OpsLoginIntent {
    data class EmailChanged(val value: String) : OpsLoginIntent
    data class PasswordChanged(val value: String) : OpsLoginIntent
    data object Submit : OpsLoginIntent
}

/** One form, one in-flight operation. Credentials are cleared on disposal or successful sign-in. */
internal class OpsLoginStore(
    private val scope: CoroutineScope,
    initialFailure: String? = null,
    private val signIn: suspend (String, String) -> Unit,
    private val onSignedIn: () -> Unit,
) {
    var state by mutableStateOf(OpsLoginState(failure = initialFailure))
        private set
    private var active = true
    private var request: Job? = null

    fun accept(intent: OpsLoginIntent) {
        if (!active || state.submitting || state.completed) return
        when (intent) {
            is OpsLoginIntent.EmailChanged -> state = state.copy(email = intent.value, failure = null)
            is OpsLoginIntent.PasswordChanged -> state = state.copy(password = intent.value, failure = null)
            OpsLoginIntent.Submit -> submit()
        }
    }

    private fun submit() {
        val email = state.email.trim()
        val password = state.password
        if (email.isBlank() || password.isBlank()) {
            state = state.copy(failure = "Email and password are required.")
            return
        }
        // Another click can arrive before Compose disables the button.
        state = state.copy(email = email, submitting = true, failure = null)
        request = scope.launch {
            try {
                signIn(email, password)
                ensureActive()
                if (active) {
                    state = state.copy(password = "", submitting = false, completed = true)
                    onSignedIn()
                }
            } catch (cancelled: CancellationException) {
                if (active) state = state.copy(submitting = false)
                throw cancelled
            } catch (failure: Throwable) {
                if (active) state = state.copy(
                    submitting = false,
                    failure = when ((failure as? ApiException)?.status) {
                        400, 401 -> "The email or password was not accepted. Check both and try again."
                        403 -> "Sign-in is unavailable for this account. Use an operator account."
                        429 -> "Too many sign-in attempts. Wait a moment and try again."
                        else -> "Could not complete sign-in. Check your connection and try again."
                    },
                )
            }
        }
    }

    fun dispose() {
        active = false
        request?.cancel()
        state = state.copy(password = "", submitting = false)
    }
}
